import { useEffect, useState, useCallback } from "react";
import { Save, Plus, Trash2, Pencil, Check, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const QTY = Array.from({ length: 21 }, (_, n) => n);

export default function DailyInventory() {
  const { user } = useAuth();
  const isOwner = user?.role !== "staff";
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newCol, setNewCol] = useState({ name: "", type: "qty" });

  useEffect(() => { api.get("/daily-inventory/config").then((r) => setConfig(r.data)); }, []);

  const loadLog = useCallback((d) => {
    api.get(`/daily-inventory/log/${d}`).then((r) => { setValues(r.data.values || {}); setDirty(false); });
  }, []);
  useEffect(() => { loadLog(date); }, [date, loadLog]);

  const cell = (itemId, colId) => values?.[itemId]?.[colId] ?? "";
  const setCell = (itemId, colId, v) => {
    setValues((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [colId]: v } }));
    setDirty(true);
  };

  const saveLog = async () => {
    setSaving(true);
    try { await api.put(`/daily-inventory/log/${date}`, { values }); toast.success("Daily count saved"); setDirty(false); }
    catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  const saveConfig = async (next) => {
    setConfig(next);
    try { await api.put("/daily-inventory/config", { columns: next.columns, items: next.items }); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const addItem = () => saveConfig({ ...config, items: [...config.items, { id: uid(), name: "New item" }] });
  const renameItem = (id, name) => setConfig((c) => ({ ...c, items: c.items.map((i) => i.id === id ? { ...i, name } : i) }));
  const commitItems = () => saveConfig(config);
  const delItem = (id) => saveConfig({ ...config, items: config.items.filter((i) => i.id !== id) });

  const addColumn = () => {
    if (!newCol.name.trim()) return toast.error("Enter a column name");
    saveConfig({ ...config, columns: [...config.columns, { id: uid(), name: newCol.name.trim(), type: newCol.type }] });
    setNewCol({ name: "", type: "qty" });
  };
  const renameCol = (id, name) => setConfig((c) => ({ ...c, columns: c.columns.map((x) => x.id === id ? { ...x, name } : x) }));
  const delCol = (id) => saveConfig({ ...config, columns: config.columns.filter((x) => x.id !== id) });

  if (!config) return <Loading />;

  const renderCell = (item, col) => {
    const v = cell(item.id, col.id);
    if (col.type === "qty") {
      return (
        <select data-testid={`di-cell-${item.id}-${col.id}`} value={v}
          onChange={(e) => setCell(item.id, col.id, e.target.value)}
          className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
          <option value="">–</option>
          {QTY.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      );
    }
    if (col.type === "number") {
      return <Input data-testid={`di-cell-${item.id}-${col.id}`} type="number" value={v} onChange={(e) => setCell(item.id, col.id, e.target.value)} className="h-9" />;
    }
    return <Input data-testid={`di-cell-${item.id}-${col.id}`} value={v} onChange={(e) => setCell(item.id, col.id, e.target.value)} className="h-9" placeholder="—" />;
  };

  return (
    <div data-testid="daily-inventory-page">
      <PageHeader title="Daily Inventory" subtitle="Log your counts for each day. Pick a date and fill the sheet."
        action={
          <div className="flex items-center gap-2">
            {isOwner && (
              <Button variant="outline" data-testid="di-edit-toggle" onClick={() => { if (editMode) commitItems(); setEditMode(!editMode); }}>
                {editMode ? <><Check className="h-4 w-4 mr-1" /> Done editing</> : <><Pencil className="h-4 w-4 mr-1" /> Edit sheet</>}
              </Button>
            )}
            <Button data-testid="di-save" onClick={saveLog} disabled={saving || !dirty} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save count</>}
            </Button>
          </div>
        } />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <input type="date" data-testid="di-date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-transparent text-sm focus:outline-none" />
        </div>
        {dirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
      </div>

      {editMode && (
        <div className="bg-accent/30 border border-accent rounded-xl p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="di-add-column-bar">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">New column name</p>
            <Input value={newCol.name} onChange={(e) => setNewCol({ ...newCol, name: e.target.value })} placeholder="e.g. Wasted" className="h-9 w-48" data-testid="di-newcol-name" />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Type</p>
            <Select value={newCol.type} onValueChange={(v) => setNewCol({ ...newCol, type: v })}>
              <SelectTrigger className="h-9 w-36" data-testid="di-newcol-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="qty">Quantity (0–20)</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={addColumn} data-testid="di-add-column" variant="outline"><Plus className="h-4 w-4 mr-1" /> Add column</Button>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              <th className="text-left font-semibold px-4 py-3 min-w-[180px] sticky left-0 bg-muted/50">Item</th>
              {config.columns.map((col) => (
                <th key={col.id} className="text-left font-semibold px-3 py-3 min-w-[130px]" data-testid={`di-col-${col.id}`}>
                  {editMode ? (
                    <div className="flex items-center gap-1">
                      <Input value={col.name} onChange={(e) => renameCol(col.id, e.target.value)} onBlur={() => saveConfig(config)} className="h-8" />
                      <button onClick={() => delCol(col.id)} data-testid={`di-del-col-${col.id}`} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <span>{col.name}<span className="ml-1 text-[10px] uppercase text-muted-foreground">{col.type === "text" ? "" : col.type === "qty" ? "qty" : "#"}</span></span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {config.items.map((item) => (
              <tr key={item.id} className="border-b border-border last:border-0 hover:bg-muted/20" data-testid={`di-item-${item.id}`}>
                <td className="px-4 py-2 font-medium sticky left-0 bg-card">
                  {editMode ? (
                    <div className="flex items-center gap-1">
                      <Input value={item.name} onChange={(e) => renameItem(item.id, e.target.value)} onBlur={() => saveConfig(config)} className="h-8" />
                      <button onClick={() => delItem(item.id)} data-testid={`di-del-item-${item.id}`} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ) : item.name}
                </td>
                {config.columns.map((col) => (
                  <td key={col.id} className="px-3 py-2">{renderCell(item, col)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editMode && (
        <Button onClick={addItem} data-testid="di-add-item" variant="outline" className="mt-4"><Plus className="h-4 w-4 mr-1" /> Add item (row)</Button>
      )}
      {config.items.length === 0 && <p className="text-center text-muted-foreground mt-6">No items yet. Click "Edit sheet" then "Add item" to start.</p>}
    </div>
  );
}
